import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Lock } from "lucide-react";

const stripeCache = {};
const getStripe = (key) => {
  if (!stripeCache[key]) stripeCache[key] = loadStripe(key);
  return stripeCache[key];
};

const appearance = {
  theme: "flat",
  variables: {
    colorPrimary: "#1F0D0A",
    colorBackground: "#FFFFFF",
    colorText: "#160B09",
    colorDanger: "#7F4142",
    fontFamily: "'DM Sans', system-ui, sans-serif",
    borderRadius: "6px",
  },
  rules: { ".Input": { border: "1px solid #D9CBBE" }, ".Input:focus": { border: "1px solid #1F0D0A", boxShadow: "none" } },
};

function PayForm({ bookingId, amount, onPaid, customer }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setError("");
    const { error: payError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/booking/confirmed?booking_id=${bookingId}` },
      redirect: "if_required",
    });
    if (payError) {
      setError(payError.message || "Your payment could not be completed.");
      setBusy(false);
      return;
    }
    await onPaid();
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="stripe-payment-form">
      <div data-testid="stripe-payment-element"><PaymentElement options={{ layout: "tabs", defaultValues: { billingDetails: { name: customer?.name || "", address: { country: "AU" } } } }} /></div>
      {error && <p className="text-sm text-mocha" role="alert" data-testid="stripe-payment-error">{error}</p>}
      <button type="submit" disabled={!stripe || busy} className={`w-full rounded-full bg-espresso text-bone py-3 text-sm transition-colors ${busy || !stripe ? "opacity-60" : "hover:bg-ink"}`} data-testid="stripe-payment-submit">
        {busy ? "Processing…" : `Pay $${Number(amount).toFixed(2)} AUD`}
      </button>
      <p className="flex items-center justify-center gap-1.5 text-[11px] text-ink/50" data-testid="stripe-payment-secure-note"><Lock size={11} /> Secured by Stripe. FYLLR never sees your card details.</p>
    </form>
  );
}

export const StripePaymentForm = ({ publishableKey, clientSecret, ...props }) => (
  <Elements stripe={getStripe(publishableKey)} options={{ clientSecret, appearance }}>
    <PayForm {...props} />
  </Elements>
);
